'use client'

import { useState } from 'react'
import Image from 'next/image'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Shield,
  Check,
  ChevronDown,
  ChevronUp,
  Monitor,
  BookOpen,
  Mic,
  Brain,
  GraduationCap,
  BarChart3,
  Globe,
  Zap,
  Target,
  CheckCircle2,
  Sparkles,
  Heart,
  Users,
} from 'lucide-react'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' as const },
  }),
}

// ============================================================
// ENTREGAVEIS
// ============================================================

const entregaveis = [
  {
    categoria: 'Plataforma e Posicionamento Digital',
    icon: Monitor,
    cor: 'teal',
    items: [
      { nome: 'Sistema web com a marca Dr. Prieto / Academia da Mente', desc: 'Plataforma completa com login, área de membros, checkout, gestão de alunos, dashboard de admin. Domínio próprio e visual alinhado à sua identidade.' },
      { nome: 'Máquina de páginas ilimitadas', desc: 'Landing pages, páginas de venda, captura, obrigado, webinário criadas dentro da própria plataforma. Sem limite, sem plugin externo.' },
      { nome: 'Checkout integrado com Asaas', desc: 'PIX, cartão e parcelamento caindo direto no seu banco. Assinatura recorrente para a Academia da Mente, venda única para o Kit Despertar, e por aí vai.' },
      { nome: 'Blog profissional integrado', desc: 'Estrutura SEO pronta para receber os 60 artigos. Ranqueamento orgânico, captação contínua.' },
    ],
  },
  {
    categoria: 'Inteligência Artificial própria',
    icon: Brain,
    cor: 'purple',
    items: [
      { nome: 'Segundo cérebro Dr. Prieto em Obsidian', desc: 'Vault estruturado com o e-book, livro autoral, transcrições das palestras, metodologia dos 4 pilares, etapas do Imparável e seus casos reais de transformação.' },
      { nome: 'IA conectada ao seu contexto', desc: 'Não responde com a internet genérica. Responde com o cérebro do Prieto. Produz roteiros, copies, respostas a alunos e conteúdos no seu tom de voz.' },
      { nome: 'Treinamento hands-on de IA', desc: 'Você aprende a usar a IA no dia a dia. Sem dependência técnica, sem ficar refém de ninguém.' },
    ],
  },
  {
    categoria: 'Metodologia empacotada e nomeada',
    icon: Sparkles,
    cor: 'rose',
    items: [
      { nome: 'Naming e identidade da metodologia', desc: 'Você sai com a metodologia batizada. Nome próprio que sintetiza os 4 pilares (consciência, comunicação, capacidade, realização) sob uma marca que é sua.' },
      { nome: 'Passaporte de Contexto Dr. Prieto', desc: 'Já entregue. Documento estratégico com persona, dores, conquistas, objeções, segmentos e posicionamento do seu negócio.' },
      { nome: 'Mapa dos 6 segmentos de atuação', desc: 'Também já entregue. Executivos, líderes corporativos, coaches, saúde em burnout, alta performance e mulheres em reinvenção pós-40.' },
    ],
  },
  {
    categoria: 'Três livros publicados',
    icon: BookOpen,
    cor: 'amber',
    items: [
      { nome: 'Livro "Do Eu ao Infinito" (autoral)', desc: 'Finalização editorial do seu livro autoral, revisão, diagramação profissional 14x21cm para gráfica e integração com impressão sob demanda.' },
      { nome: 'Livro "Supere Seus Limites" (método)', desc: 'Criação do livro de metodologia da palestra, cobrindo os 4 pilares. Escrita assistida por IA com sua voz autoral, revisão e diagramação.' },
      { nome: 'Livro "Imparável" (transformação)', desc: 'Criação do livro do programa de 4 etapas (consciência, reconfiguração, novos hábitos, transbordar). Mesmo processo editorial.' },
      { nome: 'Impressão de 2 cópias físicas de cada livro', desc: '6 livros impressos entregues em sua casa. Conectado com gráfica sob demanda - cliente compra, gráfica imprime e envia. Você não gerencia estoque.' },
    ],
  },
  {
    categoria: 'Curso e programa flagship',
    icon: GraduationCap,
    cor: 'blue',
    items: [
      { nome: 'Programa Imparável online (estruturação completa)', desc: 'Cronograma das 12 semanas, divisão pelas 4 etapas, roteiros de cada aula em 4 formatos: slide, HTML visual, áudio (Google TTS profissional) e teleprompter para gravação.' },
      { nome: 'Workshop gravado "Supere Seus Limites"', desc: 'Versão condensada da palestra de 2 horas, produzida e pronta para integrar ao Kit Despertar como primeira experiência do aluno.' },
      { nome: 'Estruturação da mentoria 1:1', desc: 'Escopo, cronograma e material de apoio do programa de mentoria individual de 6 meses (produto de high ticket).' },
    ],
  },
  {
    categoria: 'Funil e esteira digital',
    icon: Target,
    cor: 'emerald',
    items: [
      { nome: 'Webinário perpétuo de 45 minutos', desc: 'Gravação, edição e implantação de "Supere Seus Limites" no formato webinário. Roda 24/7 capturando lead e oferecendo o Kit Despertar.' },
      { nome: 'Kit Despertar (R$97) completo', desc: 'Página de venda + checkout + entrega automática do livro digital + workshop gravado + Passaporte do Despertar (diagnóstico guiado).' },
      { nome: 'Sequências de e-mail e WhatsApp', desc: '12 e-mails de nutrição escritos com sua voz (da isca até o Imparável) e mensagens de acompanhamento pós-evento e pós-compra.' },
      { nome: 'Páginas da Academia da Mente e do Imparável', desc: 'Landing + checkout + onboarding da comunidade semestral (R$297) e VSL + objeções + bônus do programa flagship.' },
      { nome: 'Captura digital nas 12 palestras presenciais', desc: 'QR code no fim de cada palestra que cai direto na sequência digital. O feedback que hoje evapora (30+ mensagens só em Mogi Mirim) passa a virar lead capturado.' },
    ],
  },
  {
    categoria: 'Marketing de Conteúdo',
    icon: BarChart3,
    cor: 'indigo',
    items: [
      { nome: '60 artigos SEO', desc: 'Escritos com base no seu Passaporte de Contexto. Keywords como "sair do piloto automático", "reprogramação mental", "PNL aplicada", "burnout silencioso", "tomada de consciência" e nas dores das 6 personas dos seus segmentos. Todos passam por anti-IA review.' },
      { nome: 'Podcast resumo do livro e curso', desc: 'Episódios em áudio profissional condensando os 4 pilares e as 4 etapas. Alcança quem prefere consumo auditivo.' },
    ],
  },
  {
    categoria: 'Suporte e acompanhamento',
    icon: Heart,
    cor: 'pink',
    items: [
      { nome: '12 semanas de acompanhamento direto', desc: 'Suporte via WhatsApp comigo para qualquer ajuste ou dúvida durante toda a implantação.' },
      { nome: 'Sessões de revisão por bloco', desc: 'Ao final de cada bloco de entregas você aprova antes de avançar. Sem surpresa no final.' },
      { nome: 'Documentação completa em Obsidian', desc: 'Tudo o que for entregue fica registrado na sua máquina. Você fica com controle total.' },
    ],
  },
]

// ============================================================
// CRONOGRAMA
// ============================================================

const cronograma = [
  {
    semana: 'Semanas 1-2',
    fase: 'Fundação e Descoberta',
    items: ['Setup IA + Obsidian + segundo cérebro Dr. Prieto', 'Naming e identidade da metodologia', 'Estrutura da plataforma + domínio + checkout', 'Revisão do Passaporte de Contexto'],
    cor: 'teal',
  },
  {
    semana: 'Semanas 3-6',
    fase: 'Produção de Conteúdo',
    items: ['Roteiros do Imparável nas 4 etapas (4 formatos cada)', 'Livro "Supere Seus Limites" escrito', 'Livro "Imparável" escrito', 'Finalização do livro "Do Eu ao Infinito"', 'Primeiros artigos do blog'],
    cor: 'blue',
  },
  {
    semana: 'Semanas 7-9',
    fase: 'Plataforma e Funil',
    items: ['Deploy da plataforma com a marca Dr. Prieto', 'Área de membros + landing pages + checkout', 'Kit Despertar funcional', 'Webinário perpétuo gravado e no ar', 'Integração de pagamentos'],
    cor: 'purple',
  },
  {
    semana: 'Semanas 10-12',
    fase: 'Lançamento e Escala',
    items: ['6 livros impressos (2 cópias de cada)', 'Blog com 60 artigos publicados', 'Podcast publicado', 'Funil evergreen ativo', 'Sequências de e-mail e WhatsApp rodando', 'Onboarding da Academia da Mente e do Imparável'],
    cor: 'amber',
  },
]

// ============================================================
// COMPARATIVO DE VALOR
// ============================================================

const comparativo = [
  { item: 'Plataforma web personalizada (Next.js + Supabase)', valorMercado: 'R$ 8.000 - R$ 15.000' },
  { item: 'Setup IA + segundo cérebro Obsidian', valorMercado: 'R$ 2.000 - R$ 3.000' },
  { item: 'Naming + identidade de metodologia', valorMercado: 'R$ 3.000 - R$ 6.000' },
  { item: 'Livro "Do Eu ao Infinito" (finalização + diagramação)', valorMercado: 'R$ 2.000 - R$ 4.000' },
  { item: 'Livro "Supere Seus Limites"', valorMercado: 'R$ 4.000 - R$ 8.000' },
  { item: 'Livro "Imparável"', valorMercado: 'R$ 4.000 - R$ 8.000' },
  { item: 'Estruturação do Imparável online (4 formatos)', valorMercado: 'R$ 3.000 - R$ 6.000' },
  { item: 'Funil evergreen + webinário + Kit Despertar', valorMercado: 'R$ 3.000 - R$ 5.000' },
  { item: 'Blog com 60 artigos SEO anti-IA', valorMercado: 'R$ 6.000 - R$ 12.000' },
  { item: 'Podcast profissional', valorMercado: 'R$ 2.000 - R$ 4.000' },
  { item: 'Sequências de e-mail e WhatsApp', valorMercado: 'R$ 1.500 - R$ 3.000' },
]

// ============================================================
// DOR -> SOLUCAO (ancoragem no passaporte)
// ============================================================

const dorSolucao = [
  { dor: 'Metodologia sem nome', solucao: 'Naming + identidade + 3 livros publicados cobrindo o método' },
  { dor: 'Livro autoral descolado do método', solucao: 'Livro autoral finalizado + 2 livros novos com o método completo' },
  { dor: 'Zero esteira digital', solucao: 'Esteira completa: webinário, Kit Despertar, comunidade, Imparável e high ticket' },
  { dor: 'Feedback que evapora após as palestras', solucao: 'QR code nas 12 palestras + sequência de nutrição automática' },
  { dor: 'Gargalo pessoal (agenda cheia)', solucao: 'Programa flagship online + automações + blog SEO trazendo lead 24/7' },
  { dor: 'Loop de soluções que não resolvem', solucao: 'Plataforma própria, sua marca, você no comando do que entrega e como entrega' },
]

// ============================================================
// FAQ
// ============================================================

const faq = [
  {
    pergunta: 'Em quanto tempo o projeto fica pronto?',
    resposta: 'O cronograma previsto é de 12 semanas (3 meses). Na prática, a primeira versão operacional já está no ar em 1 a 2 semanas. As 12 semanas são a janela completa de suporte, refinamento e produção dos livros e do blog.',
  },
  {
    pergunta: 'Preciso ter conhecimento técnico?',
    resposta: 'Não. Toda a parte técnica (plataforma, integrações, deploy, IA) fica por minha conta. O que eu preciso de você é o seu conhecimento sobre PNL, hipnose clínica, os 4 pilares e as 4 etapas do Imparável. O Passaporte de Contexto já captura boa parte disso.',
  },
  {
    pergunta: 'O sistema é meu? Tenho controle total?',
    resposta: 'Sim. No plano completo (R$20k), a plataforma fica no seu domínio, com seu admin, sua marca, seu servidor. No plano inicial (R$10k), o ecossistema fica na minha infraestrutura, mas a base já sai pronta para você exportar quando quiser migrar.',
  },
  {
    pergunta: 'E se eu quiser mudar algo depois?',
    resposta: 'A plataforma é construída em Next.js, React e Supabase (código aberto). Qualquer desenvolvedor pode dar manutenção. Além disso, com a IA instalada, você mesmo pode fazer ajustes guiados pela própria ferramenta.',
  },
  {
    pergunta: 'Os 60 artigos do blog são genéricos?',
    resposta: 'Não. Cada artigo é produzido com base no seu Passaporte de Contexto, otimizado para SEO e escrito com técnicas anti-IA para garantir autenticidade. São artigos que ranqueiam no Google e geram tráfego orgânico.',
  },
  {
    pergunta: 'Como funciona o pagamento na opção A (indicação)?',
    resposta: 'Você não paga nada de entrada. A cada contrato que for fechado por indicação sua, 10% abate o valor do projeto, até quitar os R$10.000. O único custo paralelo é o domínio (~R$40 por ano).',
  },
  {
    pergunta: 'Como funciona o pagamento na opção B (infra própria)?',
    resposta: 'Investimento total de R$20.000. Entrada de R$10.000 via PIX ou cartão em até 10x, e o restante em 5 parcelas semanais de R$2.000 via link seguro do Asaas. Você também paga servidor (~US$10/mês) e a IA Claude (R$100 a R$500/mês).',
  },
  {
    pergunta: 'E se eu não gostar do resultado?',
    resposta: 'Cada bloco é aprovado por você antes de avançar. Se em algum ponto o resultado não te atender, ajustamos até ficar do jeito que precisa. Nada é entregue sem o seu sim.',
  },
  {
    pergunta: 'Vocês já fizeram algo parecido?',
    resposta: 'Sim. A mesma base de plataforma roda o app.ebuz.com.br, o app.antoniofogaca.com.br e outros ecossistemas. Já produzi mais de 6 livros com diagramação profissional e 140+ aulas de cursos publicados. O Dr. Ralph está no mesmo pacote e o case dele está em app.ebuz.com.br/ralph.',
  },
  {
    pergunta: 'O que é o Passaporte de Contexto?',
    resposta: 'É o documento estratégico que mapeia persona, dores, conquistas, objeções e segmentos do seu negócio, usando a Metodologia APE. Ele serve como base para todos os conteúdos: livros, curso, artigos, copy. Garante consistência em tudo que é produzido. O seu já está pronto e você consegue vê-lo na biblioteca da sua conta.',
  },
]

// ============================================================
// COR HELPER
// ============================================================

const coresConfig = {
  teal: { light: 'bg-teal-50', badge: 'bg-teal-100 text-teal-700', text: 'text-teal-600', border: 'border-teal-100' },
  purple: { light: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', text: 'text-purple-600', border: 'border-purple-100' },
  blue: { light: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700', text: 'text-blue-600', border: 'border-blue-100' },
  amber: { light: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', text: 'text-amber-600', border: 'border-amber-100' },
  rose: { light: 'bg-rose-50', badge: 'bg-rose-100 text-rose-700', text: 'text-rose-600', border: 'border-rose-100' },
  emerald: { light: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-600', border: 'border-emerald-100' },
  indigo: { light: 'bg-indigo-50', badge: 'bg-indigo-100 text-indigo-700', text: 'text-indigo-600', border: 'border-indigo-100' },
  pink: { light: 'bg-pink-50', badge: 'bg-pink-100 text-pink-700', text: 'text-pink-600', border: 'border-pink-100' },
}

// ============================================================
// COMPONENTE
// ============================================================

export default function PrietoPropostaPage() {
  const [faqAberto, setFaqAberto] = useState<number | null>(null)
  const totalMercado = 'R$ 38.500 a R$ 74.000'

  return (
    <main className="min-h-screen bg-[#f8fafb]">
      {/* Header */}
      <header className="w-full py-4 px-6 border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-5xl flex items-center gap-3">
          <Image
            src="/images/jonathan-machado.jpg"
            alt="Jonathan Machado"
            width={40}
            height={40}
            className="rounded-full"
          />
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-gray-900 leading-tight">Jonathan Machado</span>
            <span className="text-xs text-gray-400">Proposta Comercial - Dr. Prieto</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 pt-10 pb-6 md:pt-16 text-center">
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 px-4 py-1.5 text-sm font-medium text-teal-700 mb-6">
            <Shield className="h-3.5 w-3.5" />
            Proposta exclusiva - Dr. Prieto (Academia da Mente)
          </span>
        </motion.div>

        <motion.h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight"
          variants={fadeUp} initial="hidden" animate="visible" custom={1}
        >
          Sua esteira de produtos completa
          <br />
          <span className="text-teal-600">para tirar o Dr. Prieto do palco e colocar no ecossistema</span>
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-6"
          variants={fadeUp} initial="hidden" animate="visible" custom={2}
        >
          Plataforma, IA própria, três livros, programa Imparável online, webinário perpétuo, blog SEO e captura das 12 palestras.
          Tudo ancorado no seu Passaporte de Contexto.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-gray-500"
          variants={fadeUp} initial="hidden" animate="visible" custom={3}
        >
          <span className="flex items-center gap-1.5"><Monitor className="h-4 w-4 text-teal-500" /> Plataforma própria</span>
          <span className="flex items-center gap-1.5"><Brain className="h-4 w-4 text-purple-500" /> IA com seu cérebro</span>
          <span className="flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-amber-500" /> 3 livros impressos</span>
          <span className="flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-blue-500" /> Imparável online</span>
          <span className="flex items-center gap-1.5"><Globe className="h-4 w-4 text-emerald-500" /> 60 artigos SEO</span>
          <span className="flex items-center gap-1.5"><Mic className="h-4 w-4 text-indigo-500" /> Podcast</span>
        </motion.div>
      </section>

      {/* Cenário atual */}
      <section className="mx-auto max-w-4xl px-4 pb-8">
        <motion.div
          className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm"
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Seu cenário atual</h3>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              Você tem <span className="font-semibold text-gray-900">12 palestras agendadas até junho</span>,
              uma metodologia vivida (4 pilares de &quot;Supere Seus Limites&quot; + 4 etapas do &quot;Imparável&quot;),
              um <span className="font-semibold text-gray-900">livro autoral em finalização</span> e o feedback real
              de transformação acontecendo no mesmo dia (30+ mensagens em 24h só em Mogi Mirim).
            </p>
            <p>
              O que falta não é mensagem nem audiência. É{' '}
              <span className="font-semibold text-teal-700">infraestrutura para que essa audiência pare de evaporar</span>.
              Esta proposta entrega o ecossistema digital completo, transformando o circuito presencial em uma esteira que captura,
              nutre, vende e escala 24 horas por dia. Sem você precisar estar em cima do palco para faturar.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                <p className="font-semibold text-teal-800 text-xs uppercase tracking-wide mb-1">Propósito</p>
                <p className="text-teal-700">Resgatar pessoas da escuridão que elas nem sabem que vivem. Tomada de consciência, reprogramação mental e novos hábitos.</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="font-semibold text-blue-800 text-xs uppercase tracking-wide mb-1">Persona núcleo</p>
                <p className="text-blue-700">Adulto bem-sucedido por fora, em colapso silencioso por dentro. Executivos, empresários, profissionais liberais em piloto automático.</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <p className="font-semibold text-amber-800 text-xs uppercase tracking-wide mb-1">Diferencial</p>
                <p className="text-amber-700">Mais de uma década em PNL, hipnose clínica, coaching e neurociência aplicada, vivida na pele antes de ensinar.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Entregáveis */}
      <section className="mx-auto max-w-5xl px-4 pb-16" id="entregaveis">
        <motion.div
          className="text-center mb-10"
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">O que está incluso no projeto</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">Ecossistema completo, ancorado no Passaporte de Contexto e nos 6 segmentos mapeados.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {entregaveis.map((grupo, i) => {
            const cores = coresConfig[grupo.cor as keyof typeof coresConfig]
            const Icon = grupo.icon
            return (
              <motion.div
                key={grupo.categoria}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              >
                <div className={`${cores.light} px-6 py-4 border-b border-gray-100 flex items-center gap-3`}>
                  <div className="bg-white rounded-xl p-2 shrink-0">
                    <Icon className={`h-5 w-5 ${cores.text}`} />
                  </div>
                  <h3 className="font-bold text-gray-900">{grupo.categoria}</h3>
                  <span className={`ml-auto text-xs font-medium ${cores.badge} px-2.5 py-1 rounded-full`}>
                    {grupo.items.length} {grupo.items.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>
                <div className="divide-y divide-gray-50">
                  {grupo.items.map((item, ii) => (
                    <div key={ii} className="px-6 py-4 flex items-start gap-3">
                      <Check className={`h-4 w-4 ${cores.text} mt-1 shrink-0`} />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{item.nome}</p>
                        <p className="text-gray-600 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* Cronograma */}
      <section className="bg-white py-16 border-y border-gray-100">
        <div className="mx-auto max-w-5xl px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Cronograma de 12 semanas</h2>
            <p className="text-gray-600">4 blocos de entrega com aprovação sua ao final de cada um.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cronograma.map((fase, i) => {
              const cores = coresConfig[fase.cor as keyof typeof coresConfig]
              return (
                <motion.div
                  key={fase.semana}
                  className="bg-gray-50 rounded-2xl p-5 border border-gray-100"
                  variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
                >
                  <div className="flex flex-col gap-1 mb-3">
                    <span className={`text-xs font-bold ${cores.badge} px-2.5 py-1 rounded-full w-fit`}>{fase.semana}</span>
                    <h4 className="font-bold text-gray-900">{fase.fase}</h4>
                  </div>
                  <ul className="space-y-2">
                    {fase.items.map((item, ii) => (
                      <li key={ii} className="flex items-start gap-2 text-xs text-gray-700">
                        <CheckCircle2 className={`h-3.5 w-3.5 ${cores.text} mt-0.5 shrink-0`} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Comparativo */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Valor de mercado de cada entrega</h2>
          <p className="text-gray-600">Referência do que custaria contratando cada peça separadamente.</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="divide-y divide-gray-100">
            {comparativo.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-6 py-4">
                <span className="text-sm text-gray-800">{item.item}</span>
                <span className="text-sm font-semibold text-gray-900 shrink-0 ml-4">{item.valorMercado}</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <span className="font-bold text-gray-900">Total de mercado</span>
            <span className="font-bold text-gray-900">{totalMercado}</span>
          </div>
          <div className="bg-teal-50 px-6 py-5 flex items-center justify-between">
            <span className="font-bold text-teal-900 text-lg">Seu investimento</span>
            <span className="font-bold text-teal-900 text-lg">A partir de R$ 10.000</span>
          </div>
        </div>
      </section>

      {/* Dor -> Solucao */}
      <section className="bg-gray-900 py-16">
        <div className="mx-auto max-w-4xl px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Como cada entrega resolve uma dor do seu Passaporte</h2>
            <p className="text-gray-400">Ancoragem direta nas dores que você mesmo relatou no Passaporte de Contexto.</p>
          </div>
          <div className="space-y-3">
            {dorSolucao.map((item, i) => (
              <motion.div
                key={i}
                className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-800 rounded-xl p-5 border border-gray-700"
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              >
                <div>
                  <p className="text-xs uppercase tracking-wide text-red-400 font-semibold mb-1">Dor</p>
                  <p className="text-sm text-gray-200">{item.dor}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-teal-400 font-semibold mb-1">Como a proposta resolve</p>
                  <p className="text-sm text-gray-200">{item.solucao}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Planos */}
      <section className="mx-auto max-w-4xl px-4 py-16" id="investimento">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Duas formas de começar</h2>
          <p className="text-gray-600">Escolha o modelo que faz mais sentido para o seu momento financeiro.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Opção A */}
          <div className="relative bg-white rounded-2xl border-2 border-teal-500 p-6 shadow-md">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
              Recomendado para 4 meses de prazo
            </span>
            <h3 className="font-bold text-gray-900 text-lg">Opção A - Infra na eBuz</h3>
            <p className="text-sm text-gray-500 mt-1">Zero entrada, 100% por comissão de indicação</p>
            <div className="mt-4 mb-6">
              <div className="text-3xl font-bold text-gray-900">R$ 10.000</div>
              <div className="text-sm text-gray-500 mt-1">abatidos com 10% por contrato indicado até quitar</div>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" /> Todas as entregas inclusas</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" /> Ecossistema na infraestrutura eBuz</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" /> Zero entrada e zero custo mensal de servidor</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" /> Pagamento por indicação (10% por contrato fechado)</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" /> Você paga apenas o domínio (~R$40/ano)</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" /> Base pronta para migrar para infra própria depois</li>
            </ul>
            <a
              href="https://wa.me/5515991914069?text=Ol%C3%A1%20Jonathan%2C%20quero%20fechar%20a%20Op%C3%A7%C3%A3o%20A%20da%20proposta%20do%20Dr.%20Prieto"
              target="_blank"
              rel="noreferrer"
              className="mt-6 block w-full bg-teal-600 hover:bg-teal-700 text-white text-center py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]"
            >
              Aceitar Opção A
            </a>
          </div>

          {/* Opção B */}
          <div className="relative bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 text-lg">Opção B - Infra própria</h3>
            <p className="text-sm text-gray-500 mt-1">Independência total, sem depender de ninguém</p>
            <div className="mt-4 mb-6">
              <div className="text-3xl font-bold text-gray-900">R$ 20.000</div>
              <div className="text-sm text-gray-500 mt-1">R$10k sinal + 5 parcelas semanais de R$2k</div>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" /> Todas as entregas inclusas</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" /> Seu servidor, seu banco, sua IA, seu domínio</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" /> Independência total de infraestrutura</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" /> Custo mensal: servidor (~US$10) + IA (R$100-500)</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" /> Entrada de R$10k PIX ou 10x no cartão</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" /> 5 parcelas semanais de R$2k via Asaas</li>
            </ul>
            <a
              href="https://wa.me/5515991914069?text=Ol%C3%A1%20Jonathan%2C%20quero%20fechar%20a%20Op%C3%A7%C3%A3o%20B%20da%20proposta%20do%20Dr.%20Prieto"
              target="_blank"
              rel="noreferrer"
              className="mt-6 block w-full bg-gray-900 hover:bg-gray-800 text-white text-center py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]"
            >
              Aceitar Opção B
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 pb-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Perguntas frequentes</h2>
        </div>
        <div className="space-y-2">
          {faq.map((item, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-xl overflow-hidden">
              <button
                onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="font-semibold text-gray-900 text-sm">{item.pergunta}</span>
                {faqAberto === i ? (
                  <ChevronUp className="h-4 w-4 text-gray-500 shrink-0" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                )}
              </button>
              {faqAberto === i && (
                <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{item.resposta}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Final */}
      <section className="bg-teal-600 py-12 text-center">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Pronto para tirar o Dr. Prieto do palco?
          </h2>
          <p className="text-teal-100 mb-6">
            Cada semana que passa sem esteira é feedback de palestra que evapora.
          </p>
          <button
            onClick={() => document.getElementById('investimento')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-teal-700 font-semibold text-lg hover:bg-teal-50 active:scale-[0.98] transition-all cursor-pointer"
          >
            Ver planos de pagamento
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      <footer className="py-6 text-center text-sm text-gray-400">
        eBuz Negócios Digitais · Jonathan Machado · Proposta válida por 30 dias
      </footer>
    </main>
  )
}
